import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Switch, Button, Table, Tooltip } from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  CalendarOutlined,
  UserOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import { zoomAdminService } from "../../services/zoomAdminService";
import { roundRobinService } from "../../services/roundRobinService";
import { deepClone } from "../../utils/cloneUtils";
import { hasPermission } from "../../utils/roleHelpers";
import AdminBookingConfigSkeleton from "./components/Skeleton/AdminBookingConfigSkeleton";
import { createTableColumns } from "./components/TableColumns/TableColumns";
import {
  validateActiveAdminExists,
  validateInactiveAdminsNoPriority,
} from "./utils/adminBookingValidationHelpers";
import {
  VALIDATION_ERRORS,
  SUCCESS_MESSAGES,
  NOTIFICATION_DURATION,
  INFO_MESSAGES,
  PERMISSION_CONFIG,
} from "./constants/adminBookingConstants";
import {
  resetHighPriorityExcept,
  ensureOnlyActiveAdminHasHighPriority,
  handleHighPriorityDisabled,
  getActiveAdmins,
  validateActiveStatusChange,
  buildAdminConfigPayload,
  buildAdminConfigsPayload,
  disableAllHighPriority,
  savePriorityAdmins,
  restorePriorityAdmins,
  buildSavedConfigurations,
} from "./helpers";
import "./AdminBookingConfig.scss";

/**
 * Admin Booking Configuration Component
 * Manages admin availability, priority levels, daily limits, and Round Robin routing mode.
 * 
 * Features:
 * - Configure admin availability (active/inactive)
 * - Set high priority for preferred admins
 * - Configure daily booking limits
 * - Enable/disable global Round Robin distribution
 * - Real-time validation and error handling
 */
const AdminBookingConfig = ({ userInfo }) => {
  const messageApi = useMessageApi();
  const canUpdateConfig = hasPermission(userInfo, PERMISSION_CONFIG.MODULE, PERMISSION_CONFIG.ACTION);

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRoundRobinEnabled, setIsRoundRobinEnabled] = useState(false);
  const [adminConfigList, setAdminConfigList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedConfigurations, setSavedConfigurations] = useState(null);
  const [priorityAdminsBeforeRoundRobin, setPriorityAdminsBeforeRoundRobin] = useState(null);

  /**
   * Wrapper for ensureOnlyActiveAdminHasHighPriority with getActiveAdmins bound
   */
  const ensureOnlyActiveAdminHasPriority = useCallback(
    (configs, roundRobinEnabled) =>
      ensureOnlyActiveAdminHasHighPriority(configs, roundRobinEnabled, getActiveAdmins),
    []
  );

  /**
   * Load admin and Round Robin configurations from backend
   * Fetches both in parallel for better performance
   */
  const loadConfigurationsFromBackend = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const [adminConfigResponse, roundRobinResponse] = await Promise.all([
        zoomAdminService.getAllAdminConfigs(),
        roundRobinService.getRoundRobinConfig(),
      ]);

      if (!adminConfigResponse.success || !roundRobinResponse.success) {
        const errorMessage = adminConfigResponse.error || roundRobinResponse.error || VALIDATION_ERRORS.LOAD_ERROR;
        messageApi.error(errorMessage, NOTIFICATION_DURATION.ERROR);
        return;
      }

      const roundRobinEnabled = roundRobinResponse.data.roundRobinEnabled;
      let adminConfigs = adminConfigResponse.data.adminConfigs || [];

      // Round Robin mode disables all high priority options
      if (roundRobinEnabled) {
        adminConfigs = disableAllHighPriority(adminConfigs);
      } else {
        // Ensure the only active admin has high priority when Round Robin is disabled
        adminConfigs = ensureOnlyActiveAdminHasPriority(adminConfigs, false);
      }

      setIsRoundRobinEnabled(roundRobinEnabled);
      setAdminConfigList(adminConfigs);
      setSavedConfigurations(buildSavedConfigurations(adminConfigs, roundRobinEnabled, deepClone));
      setHasUnsavedChanges(false);
    } catch (error) {
      messageApi.error(
        error.message || VALIDATION_ERRORS.LOAD_ERROR,
        NOTIFICATION_DURATION.ERROR
      );
    } finally {
      setIsLoading(false);
    }
  }, [messageApi, ensureOnlyActiveAdminHasPriority]);

  // Load configurations on component mount
  useEffect(() => {
    loadConfigurationsFromBackend();
  }, [loadConfigurationsFromBackend]);

  /**
   * Handle Round Robin toggle with immediate backend sync
   * When enabled: saves current high priority admins and disables all
   * When disabled: restores previously enabled high priority admins
   */
  const handleRoundRobinToggle = useCallback(async (isEnabled) => {
    if (!canUpdateConfig) {
      messageApi.error("You don't have permission to update this configuration.", NOTIFICATION_DURATION.ERROR);
      return;
    }

    const previousAdminConfigs = adminConfigList;
    const previousPriorityAdmins = priorityAdminsBeforeRoundRobin;

    try {
      setIsSaving(true);
      
      let updatedAdminConfigs = adminConfigList;
      
      if (isEnabled) {
        // Save high priority states before disabling Round Robin
        const priorityMap = savePriorityAdmins(adminConfigList);
        setPriorityAdminsBeforeRoundRobin(priorityMap);
        
        // Disable all high priority when enabling Round Robin
        updatedAdminConfigs = disableAllHighPriority(adminConfigList);
        setAdminConfigList(updatedAdminConfigs);
      } else {
        // When disabling Round Robin, restore previously saved high priority admins
        if (priorityAdminsBeforeRoundRobin) {
          updatedAdminConfigs = restorePriorityAdmins(adminConfigList, priorityAdminsBeforeRoundRobin);
        } else {
          // If no saved state, ensure the only active admin has high priority
          updatedAdminConfigs = ensureOnlyActiveAdminHasPriority(adminConfigList, false);
        }
        setAdminConfigList(updatedAdminConfigs);
        setPriorityAdminsBeforeRoundRobin(null);
      }
      
      const adminConfigPayload = buildAdminConfigsPayload(updatedAdminConfigs);

      // Update both Round Robin and admin configs in parallel
      const [roundRobinResponse, adminConfigResponse] = await Promise.all([
        roundRobinService.updateRoundRobinConfig({ roundRobinEnabled: isEnabled }),
        zoomAdminService.updateAdminConfigs({ adminConfigs: adminConfigPayload }),
      ]);

      if (roundRobinResponse.success && adminConfigResponse.success) {
        setIsRoundRobinEnabled(isEnabled);
        messageApi.success("Configuration updated successfully", NOTIFICATION_DURATION.SUCCESS);
        
        if (savedConfigurations) {
          setSavedConfigurations(buildSavedConfigurations(updatedAdminConfigs, isEnabled, deepClone));
        }
        setHasUnsavedChanges(false);
      } else {
        const errorMessage = roundRobinResponse.error || adminConfigResponse.error || VALIDATION_ERRORS.SAVE_ERROR;
        messageApi.error(errorMessage, NOTIFICATION_DURATION.ERROR);
        
        // Revert if update failed
        setAdminConfigList(previousAdminConfigs);
        setPriorityAdminsBeforeRoundRobin(previousPriorityAdmins);
      }
    } catch (error) {
      messageApi.error(error.message || VALIDATION_ERRORS.SAVE_ERROR, NOTIFICATION_DURATION.ERROR);
      setAdminConfigList(previousAdminConfigs);
      setPriorityAdminsBeforeRoundRobin(previousPriorityAdmins);
    } finally {
      setIsSaving(false);
    }
  }, [canUpdateConfig, messageApi, adminConfigList, priorityAdminsBeforeRoundRobin, savedConfigurations, ensureOnlyActiveAdminHasPriority]);

  /**
   * Handle disabling high priority: ensure another active admin retains priority
   */
  const handleHighPriorityDisabledWrapper = useCallback(
    (configs, configId) =>
      handleHighPriorityDisabled(configs, configId, isRoundRobinEnabled, getActiveAdmins, messageApi, NOTIFICATION_DURATION),
    [isRoundRobinEnabled, messageApi]
  );

  /**
   * Handle individual admin config field changes
   * Enforces business rules:
   * - Only one admin can have high priority
   * - At least one admin must remain active
   * - The only active admin must always have high priority (when Round Robin is disabled)
   */
  const handleAdminConfigFieldChange = useCallback((configId, fieldName, newValue) => {
    setAdminConfigList((prevConfigs) => {
      // Update the specific field for target config
      let updatedConfigs = prevConfigs.map((config) => {
        if (config.id === configId) {
          return { ...config, [fieldName]: newValue };
        }
        return config;
      });

      // Business Rule: When disabling high priority, ensure another active admin gets it
      if (fieldName === "isHighPriority" && newValue === false && !isRoundRobinEnabled) {
        const result = handleHighPriorityDisabledWrapper(updatedConfigs, configId);
        if (result === null) return prevConfigs; // Revert change
        updatedConfigs = result;
      }

      // Business Rule: Only one high priority admin
      if (fieldName === "isHighPriority" && newValue === true) {
        updatedConfigs = resetHighPriorityExcept(updatedConfigs, configId);
      }

      // Business Rule: Prevent deactivating the only active admin
      if (fieldName === "isActive" && newValue === false) {
        if (!validateActiveStatusChange(updatedConfigs, newValue, messageApi, VALIDATION_ERRORS)) {
          return prevConfigs;
        }
      }

      // Business Rule: Ensure single active admin has high priority
      if (fieldName === "isActive" && !isRoundRobinEnabled) {
        const activeAdmins = getActiveAdmins(updatedConfigs);
        if (activeAdmins.length === 1) {
          updatedConfigs = resetHighPriorityExcept(updatedConfigs, activeAdmins[0].id);
        }
      }

      setHasUnsavedChanges(true);
      return updatedConfigs;
    });
  }, [messageApi, isRoundRobinEnabled, handleHighPriorityDisabledWrapper, resetHighPriorityExcept, getActiveAdmins]);

  /**
   * Convert admin config to API payload format
   */
  const buildPayload = useCallback((config) => buildAdminConfigPayload(config), []);

  /**
   * Validate and save all changes to backend
   * Performs client-side validation before submission
   */
  const handleSaveChanges = useCallback(async () => {
    if (!canUpdateConfig) {
      messageApi.error("You don't have permission to save these changes.", NOTIFICATION_DURATION.ERROR);
      return;
    }

    // Validation 1: At least one admin must be active
    const activeValidation = validateActiveAdminExists(adminConfigList);
    if (!activeValidation.isValid) {
      messageApi.error(activeValidation.error, NOTIFICATION_DURATION.ERROR);
      return;
    }

    // Validation 2: Inactive admins cannot have high priority
    const priorityValidation = validateInactiveAdminsNoPriority(adminConfigList);
    if (!priorityValidation.isValid) {
      messageApi.error(priorityValidation.error, NOTIFICATION_DURATION.ERROR);
      return;
    }

    // Ensure the only active admin has high priority before saving (when Round Robin is disabled)
    let finalAdminConfigs = adminConfigList;
    if (!isRoundRobinEnabled) {
      finalAdminConfigs = ensureOnlyActiveAdminHasPriority(adminConfigList, false);
      // Update state if changes were made
      if (JSON.stringify(finalAdminConfigs) !== JSON.stringify(adminConfigList)) {
        setAdminConfigList(finalAdminConfigs);
      }
    }

    try {
      setIsSaving(true);
      
      const adminConfigPayload = buildAdminConfigsPayload(finalAdminConfigs);

      // Update both admin configs and Round Robin in parallel
      const [adminConfigResponse, roundRobinResponse] = await Promise.all([
        zoomAdminService.updateAdminConfigs({ adminConfigs: adminConfigPayload }),
        roundRobinService.updateRoundRobinConfig({ roundRobinEnabled: isRoundRobinEnabled }),
      ]);

      if (adminConfigResponse.success && roundRobinResponse.success) {
        messageApi.success(SUCCESS_MESSAGES.CONFIG_SAVED, NOTIFICATION_DURATION.SUCCESS);
        setHasUnsavedChanges(false);
        setSavedConfigurations(buildSavedConfigurations(finalAdminConfigs, isRoundRobinEnabled, deepClone));
      } else {
        const errorMessage = adminConfigResponse.error || roundRobinResponse.error || VALIDATION_ERRORS.SAVE_ERROR;
        messageApi.error(errorMessage, NOTIFICATION_DURATION.ERROR);
      }
    } catch (error) {
      messageApi.error(error.message || VALIDATION_ERRORS.SAVE_ERROR, NOTIFICATION_DURATION.ERROR);
    } finally {
      setIsSaving(false);
    }
  }, [adminConfigList, isRoundRobinEnabled, messageApi, canUpdateConfig, ensureOnlyActiveAdminHasPriority, buildPayload]);

  /**
   * Revert all unsaved changes to last saved state
   */
  const handleDiscardChanges = useCallback(() => {
    if (savedConfigurations) {
      setIsRoundRobinEnabled(savedConfigurations.isRoundRobinEnabled);
      setAdminConfigList(deepClone(savedConfigurations.adminConfigs));
      setHasUnsavedChanges(false);
      messageApi.info(SUCCESS_MESSAGES.CHANGES_DISCARDED, NOTIFICATION_DURATION.INFO);
    }
  }, [savedConfigurations, messageApi]);

  // Generate table columns (memoized to prevent unnecessary re-renders)
  const tableColumns = useMemo(
    () =>
      createTableColumns({
        handleAdminConfigChange: handleAdminConfigFieldChange,
        loading: isLoading,
        saving: isSaving,
        roundRobinEnabled: isRoundRobinEnabled,
        canUpdate: canUpdateConfig,
      }),
    [handleAdminConfigFieldChange, isLoading, isSaving, isRoundRobinEnabled, canUpdateConfig]
  );

  if (isLoading) {
    return <AdminBookingConfigSkeleton />;
  }

  return (
    <div className="admin-booking-config-page">
      <div className="admin-booking-config">
        {/* Header Section */}
        <div className="admin-booking-config-header">
          <h2>
            <SettingOutlined />
            Admin Availability & Routing Configuration
          </h2>
          <p className="admin-booking-config-subtitle">
            Configure how Zoom bookings are distributed among administrators. Manage
            availability, priority settings, and daily booking limits.
          </p>
        </div>

        <div className="admin-booking-config-content">
          {/* Global Round Robin Toggle Section */}
          <div className="admin-booking-config-round-robin">
            <div className="round-robin-label">
              <div className="round-robin-label-text">
                <CalendarOutlined style={{ marginRight: 8, color: "#52c41a", fontSize: 18 }} />
                Global Round Robin booking distribution
              </div>
              <p className="round-robin-description">
                When enabled, bookings are automatically distributed evenly across all active admins
                in a rotating sequence. This ensures fair workload distribution by giving each admin
                equal opportunity to receive bookings. When active, High Priority settings and daily
                limits are automatically disabled for all admins.
              </p>
            </div>
            <Tooltip title={!canUpdateConfig ? INFO_MESSAGES.NO_UPDATE_PERMISSION : ""}>
              <Switch
                checked={isRoundRobinEnabled}
                onChange={handleRoundRobinToggle}
                disabled={isLoading || isSaving || !canUpdateConfig}
                size="default"
              />
            </Tooltip>
          </div>

          {/* Admin Configuration Table */}
          <div className="admin-booking-config-table">
            <Table
              columns={tableColumns}
              dataSource={adminConfigList}
              rowKey="id"
              pagination={false}
              loading={isLoading}
              locale={{
                emptyText: (
                  <div style={{ padding: "60px 20px", textAlign: "center", color: "#8c8c8c" }}>
                    <UserOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
                      No admin configurations found
                    </p>
                    <p style={{ margin: "8px 0 0 0", fontSize: 14 }}>
                      Please contact support to configure admin settings
                    </p>
                  </div>
                ),
              }}
            />
          </div>

          {/* Action Buttons - Show only when there are unsaved changes and user has update permission */}
          {hasUnsavedChanges && canUpdateConfig && (
            <div className="admin-booking-config-actions">
              <Button
                onClick={handleDiscardChanges}
                disabled={isSaving}
                icon={<ReloadOutlined />}
              >
                Discard Changes
              </Button>
              <Button
                type="primary"
                onClick={handleSaveChanges}
                loading={isSaving}
                icon={<SaveOutlined />}
              >
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBookingConfig;
